/**
 * FAQ category tab navigation: scroll to section and expand first question.
 */
class CollapsibleFaqNav {
  constructor(root) {
    this.root = root;
    this.tabs = Array.from(root.querySelectorAll('[data-faq-category-tab]'));
    this.sections = Array.from(root.querySelectorAll('[data-faq-category-section]'));
    this.reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    this.scrollOffsetExtra = 24;

    if (!this.tabs.length || !this.sections.length) return;

    this.bindTabs();
    this.bindScrollSpy();
  }

  getScrollOffset() {
    const headerHeight = parseFloat(
      getComputedStyle(document.documentElement).getPropertyValue('--header-height')
    );
    return (Number.isFinite(headerHeight) ? headerHeight : 0) + this.scrollOffsetExtra;
  }

  bindTabs() {
    this.tabs.forEach((tab) => {
      tab.addEventListener('click', () => this.activateTab(tab, { scroll: true, expandFirst: true }));
    });
  }

  activateTab(tab, { scroll = false, expandFirst = false } = {}) {
    const targetId = tab.getAttribute('data-faq-category-tab');
    const section = targetId ? document.getElementById(targetId) : null;
    if (!section) return;

    this.tabs.forEach((item) => {
      const isActive = item === tab;
      item.classList.toggle('is-active', isActive);
      item.setAttribute('aria-selected', isActive ? 'true' : 'false');
    });

    if (expandFirst) {
      this.expandFirstQuestion(section);
    }

    if (scroll) {
      requestAnimationFrame(() => {
        requestAnimationFrame(() => this.scrollToSection(section));
      });
    }
  }

  scrollToSection(section) {
    const offset = this.getScrollOffset();
    const sectionTop = section.getBoundingClientRect().top + window.scrollY;
    const maxScroll = Math.max(0, document.documentElement.scrollHeight - window.innerHeight);
    const targetTop = Math.min(Math.max(0, sectionTop - offset), maxScroll);

    window.scrollTo({
      top: targetTop,
      behavior: this.reduceMotion ? 'auto' : 'smooth',
    });
  }

  expandFirstQuestion(section) {
    const items = section.querySelectorAll('.collapsible-faq__item details');
    items.forEach((details, index) => {
      details.open = index === 0;
    });
  }

  bindScrollSpy() {
    if (!('IntersectionObserver' in window)) return;

    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((entry) => entry.isIntersecting)
          .sort((a, b) => b.intersectionRatio - a.intersectionRatio)[0];

        if (!visible) return;

        const sectionId = visible.target.id;
        const tab = this.tabs.find((item) => item.getAttribute('data-faq-category-tab') === sectionId);
        if (tab && !tab.classList.contains('is-active')) {
          this.activateTab(tab, { scroll: false, expandFirst: false });
        }
      },
      {
        root: null,
        rootMargin: '-20% 0px -55% 0px',
        threshold: [0, 0.15, 0.35, 0.5],
      }
    );

    this.sections.forEach((section) => observer.observe(section));
  }
}

function initCollapsibleFaqNav() {
  document.querySelectorAll('[data-collapsible-faq-nav]').forEach((root) => {
    if (root.collapsibleFaqNav) return;
    root.collapsibleFaqNav = new CollapsibleFaqNav(root);
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initCollapsibleFaqNav);
} else {
  initCollapsibleFaqNav();
}

document.addEventListener('shopify:section:load', initCollapsibleFaqNav);
